/**
 * Pausing our camera for the room while the phone has taken it away.
 *
 * iOS and Android both stop a backgrounded app from using the camera, and a
 * browser tab is no exception: switch to another app mid-call and capture
 * stops. Neither browser ends the track, so the device manager's `ended`
 * recovery never runs, and neither tells the SFU anything:
 *
 *  - Safari MUTES the track and its sender carries on with black frames, so
 *    everyone else saw a black tile.
 *  - Chrome on Android stops delivering frames, so everyone else saw the last
 *    one, frozen.
 *
 * Either way the receiving tiles had nothing to act on. What turns a tile into
 * the avatar is the SFU's `producerPaused`, and that is only sent when the
 * publisher pauses. So the publisher does: while the camera is interrupted the
 * producer is paused, and it resumes by itself when the camera comes back. This
 * is what Zoom, Meet and Teams show as well — your avatar while you are in
 * another app, your video again when you return, without touching anything.
 *
 * Two signals, because neither browser gives us both:
 *
 *  - **The track's `mute`.** Safari's interruption, and any platform that takes
 *    the camera away while the page is still in front.
 *  - **The page being hidden, on phones and tablets only.** Chrome marks a
 *    starved camera muted late or not at all; `visibilitychange` fires at once.
 *    Never on a desktop, where a background tab keeps its camera and switching
 *    tabs mid-meeting is ordinary.
 *
 * An ENDED track counts as interrupted too, until the device manager has opened
 * a replacement and handed it over with `setTrack`. Resuming onto a dead track
 * would tell the room the camera is back while sending it nothing.
 *
 * This never touches `camOn`. That is the user's choice; this is the system's,
 * and it has to undo itself.
 */

/** What started (or, on the way out, ended) an interruption. */
export type InterruptionCause = 'track-muted' | 'track-ended' | 'page-hidden' | 'simulated'

/** The slice of a `MediaStreamTrack` this reads, declared structurally for the tests. */
export type WatchedTrack = {
  muted: boolean
  readyState: string
  addEventListener(type: 'mute' | 'unmute' | 'ended', listener: () => void): void
  removeEventListener(type: 'mute' | 'unmute' | 'ended', listener: () => void): void
}

/** The slice of `document` this reads. */
export type VisibilitySource = {
  visibilityState: string
  addEventListener(type: 'visibilitychange', listener: () => void): void
  removeEventListener(type: 'visibilitychange', listener: () => void): void
}

export type CameraInterruptionOptions = {
  /** Treat a hidden page as having no camera. Phones and tablets only — see above. */
  pauseWhenHidden: boolean
  /**
   * Called on every transition. `cause` is what started the interruption, or on
   * the way out the one that just cleared.
   */
  onChange: (change: { interrupted: boolean; cause: InterruptionCause }) => void
  /**
   * The page is back in front and the camera is still muted `stillMutedAfterMs`
   * later. Reported, not repaired: a camera that does not come back by itself
   * has not been seen yet, and this is what will say whether it happens.
   */
  onStillMuted?: () => void
  stillMutedAfterMs?: number
  doc?: VisibilitySource
  setTimeoutFn?: (fn: () => void, ms: number) => unknown
  clearTimeoutFn?: (handle: unknown) => void
}

export type CameraInterruption = {
  /** Watch this camera from now on. Call again whenever the device manager swaps it. */
  setTrack(track: WatchedTrack | null): void
  /**
   * Force the interrupted state on, as a laptop stand-in for a phone taking the
   * camera. `false` hands control back to the real signals.
   */
  simulate(on: boolean): void
  readonly interrupted: boolean
  dispose(): void
}

/**
 * Does this browser lose the camera when its page is hidden?
 *
 * Every iOS and Android browser does; no desktop one does. iPadOS 13+ reports
 * itself as a Mac, and the touch points are what give it away — the same test
 * `useDeviceDetection` uses. DevTools device mode sends a phone's user agent, so
 * the hidden-page path can be exercised from a laptop.
 */
export function cameraStopsWhenHidden(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
): boolean {
  if (/Android|iPhone|iPad|iPod/i.test(userAgent)) return true
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1
}

const STILL_MUTED_AFTER_MS = 3_000

export function createCameraInterruption(opts: CameraInterruptionOptions): CameraInterruption {
  const doc = opts.doc ?? (typeof document === 'undefined' ? null : document)
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms))
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const stillMutedAfterMs = opts.stillMutedAfterMs ?? STILL_MUTED_AFTER_MS

  let track: WatchedTrack | null = null
  let simulated = false
  let current: InterruptionCause | null = null
  let stillMutedTimer: unknown = null
  let disposed = false

  const cause = (): InterruptionCause | null => {
    if (simulated) return 'simulated'
    if (track?.readyState === 'ended') return 'track-ended'
    if (track?.muted) return 'track-muted'
    if (opts.pauseWhenHidden && doc?.visibilityState === 'hidden') return 'page-hidden'
    return null
  }

  const evaluate = () => {
    if (disposed) return
    const next = cause()
    if ((next !== null) === (current !== null)) {
      // Still interrupted, for a different reason: keep the newer one for the
      // log line that ends it. No transition, so nobody is told.
      if (next) current = next
      return
    }
    const reported = (next ?? current) as InterruptionCause
    current = next
    opts.onChange({ interrupted: next !== null, cause: reported })
  }

  const clearStillMuted = () => {
    if (stillMutedTimer === null) return
    clearT(stillMutedTimer)
    stillMutedTimer = null
  }

  const onVisibility = () => {
    evaluate()
    clearStillMuted()
    if (doc?.visibilityState !== 'visible' || !track?.muted) return
    stillMutedTimer = setT(() => {
      stillMutedTimer = null
      if (!disposed && doc?.visibilityState === 'visible' && track?.muted) opts.onStillMuted?.()
    }, stillMutedAfterMs)
  }

  const detachTrack = () => {
    if (!track) return
    track.removeEventListener('mute', evaluate)
    track.removeEventListener('unmute', evaluate)
    track.removeEventListener('ended', evaluate)
  }

  doc?.addEventListener('visibilitychange', onVisibility)

  return {
    setTrack(next) {
      if (disposed || next === track) return
      detachTrack()
      track = next
      if (track) {
        track.addEventListener('mute', evaluate)
        track.addEventListener('unmute', evaluate)
        track.addEventListener('ended', evaluate)
      }
      evaluate()
    },
    simulate(on) {
      simulated = on
      evaluate()
    },
    get interrupted() {
      return current !== null
    },
    dispose() {
      if (disposed) return
      disposed = true
      detachTrack()
      track = null
      clearStillMuted()
      doc?.removeEventListener('visibilitychange', onVisibility)
    },
  }
}
